import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Spinner } from "react-bootstrap";
import Select from "../../Common/FormElements/Select/Select";
import Button from "../../Common/FormElements/Button/Button";
import CalendarInput from "../../Common/FormElements/CalendarInput/CalendarInput";
import QuotePill, { formatEuro, unitLabel, unitTone } from "./QuotePill";
import {
  fetchQuoteOrderPreview,
  saveProjectQuote,
} from "../../../REDUX/actions/appProjects";
import { closeCenteredModal } from "../../../REDUX/actions/centeredModal";

const OTHER_MODEL = "other-model";

/**
 * C2 "Modifier le devis": replace the unit that was sold (same model from
 * another depot) and resend — the quote goes back to "Envoyé" and the
 * customer is told it changed. "Autre modèle…" loads the quote in the
 * Devis builder instead (`onOtherModel`).
 */
const EditQuoteModal = ({ project, quote, onOtherModel }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const [preview, setPreview] = useState(null);
  const [replacements, setReplacements] = useState({});
  const [validUntil, setValidUntil] = useState(
    quote.validUntil ? new Date(quote.validUntil) : null,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    dispatch(fetchQuoteOrderPreview(project.id, quote.id, controller.signal)).then(
      (data) => {
        if (!data) return;
        setPreview(data);
        setReplacements(
          Object.fromEntries(
            data.lines
              .filter((line) => line.unavailable && line.units.length)
              .map((line) => [line.index, line.units[0].id]),
          ),
        );
      },
    );
    return () => controller.abort();
  }, [dispatch, project.id, quote.id]);

  const close = () => dispatch(closeCenteredModal());
  const customerName = preview?.customer?.name ?? "";

  const handleSave = () => {
    setSaving(true);
    const lines = (quote.lines ?? []).map((line, index) =>
      replacements[index] ? { ...line, packingId: replacements[index] } : line,
    );
    dispatch(
      saveProjectQuote(
        project.id,
        {
          quoteId: quote.id,
          lines,
          recipientId: preview?.customer?.id ?? project.mainContact?.id,
          validUntil: validUntil ? moment(validUntil).format("YYYY-MM-DD") : undefined,
        },
        t,
        close,
      ),
    ).finally(() => setSaving(false));
  };

  const missing = preview?.lines.some(
    (line) => line.unavailable && !replacements[line.index],
  );

  return (
    <div className="p-4 bg-white br10" style={{ width: 820, maxWidth: "90vw" }}>
      <h3 className="text-blue bold f18 quickSand mb-1">
        {t("Edit quote no. {{number}}", { number: quote.id })}
      </h3>
      <p className="f13 text-brightGrey mb-3">
        {customerName} · {t("Project")} N°{project.number ?? project.id}
        {preview?.organization?.name ? ` · ${preview.organization.name}` : ""}
      </p>
      {!preview ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          <div
            className="p-3 br10 mb-3 f13 sBold"
            style={{ background: "#feefd8", color: "#050505" }}
          >
            {t(
              "A line is no longer available. Choose another unit or another model: the updated quote will be sent again to {{name}}, who will have to approve it again.",
              { name: customerName },
            )}
          </div>
          <p className="f14 sBold text-ultraBlack mb-2">{t("Quote lines")}</p>
          {preview.lines.map((line) => {
            const options = [
              ...line.units.map((unit) => ({
                name: `${line.label} — ${t("depot {{depot}}", {
                  depot: unit.depot ?? "-",
                })} · ${t(unitTone(unit) === "green" ? "In stock" : "Supply in progress")} · ${formatEuro(
                  line.price,
                )}`,
                code: unit.id,
              })),
              { name: t("Another model…"), code: OTHER_MODEL },
            ];
            return (
              <div key={line.index} className="py-2 border-bottom">
                <div className="d-flex align-items-center gap-3">
                  <span
                    className="f14 sBold text-ultraBlack flex-1"
                    style={line.unavailable ? { textDecoration: "line-through" } : undefined}
                  >
                    {line.label}
                    {line.unavailable?.depot
                      ? ` — ${t("depot {{depot}}", { depot: line.unavailable.depot })}`
                      : ""}
                  </span>
                  <span className="f14" style={{ width: 110 }}>
                    {formatEuro(line.price)}
                  </span>
                  {line.unavailable ? (
                    <QuotePill tone="red">
                      {t("Unavailable — sold on {{date}}", {
                        date: line.unavailable.soldOn
                          ? moment(line.unavailable.soldOn).format("DD/MM")
                          : "-",
                      })}
                    </QuotePill>
                  ) : line.stock === "unit" && line.units[0] ? (
                    <QuotePill tone={unitTone(line.units[0])}>
                      {unitLabel(line.units[0], t)}
                    </QuotePill>
                  ) : (
                    <QuotePill tone="grey">
                      {t(line.stock === "service" ? "Service, no stock" : "No stock")}
                    </QuotePill>
                  )}
                </div>
                {line.unavailable && (
                  <div className="d-flex align-items-center gap-3 mt-2">
                    <span className="f13 sBold">{t("Replace with")}</span>
                    <div className="flex-1">
                      <Select
                        options={options}
                        value={
                          options.find((o) => o.code === replacements[line.index]) ?? null
                        }
                        callback={(e) => {
                          if (e.value?.code === OTHER_MODEL) {
                            close();
                            onOtherModel?.();
                            return;
                          }
                          setReplacements((prev) => ({
                            ...prev,
                            [line.index]: e.value?.code,
                          }));
                        }}
                        appendToSelf
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="d-flex align-items-center justify-content-between mt-4 mb-4">
            <span className="f14 sBold text-ultraBlack">
              {t("Total {{amount}} incl. VAT (unchanged)", {
                amount: formatEuro(preview.quote.totalAmount),
              })}
            </span>
            <div className="d-flex align-items-center gap-2">
              <span className="f14 sBold">{t("Valid until")}</span>
              <div style={{ width: 170 }}>
                <CalendarInput
                  value={validUntil}
                  callback={(e) => setValidUntil(e.value ?? null)}
                />
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end gap-3">
            <Button variant="outline" onClick={close}>
              {t("Cancel")}
            </Button>
            <Button
              loading={saving}
              disabled={missing}
              onClick={saving || missing ? undefined : handleSave}
            >
              {t("Save and resend the quote")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default EditQuoteModal;
